/**
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceResultParams:
 *      type: object
 *      properties:
 *        ChampionshipResultTableRaceId:
 *          description: ID of the associated ChampionshipResultTableRace
 *          type: string
 *        ChampionshipResultTableEntryId:
 *          description: ID of the associated ChampionshipResultTableEntry
 *          type: string
 *        Value:
 *          description: Value of the ChampionshipResultTableRaceResult
 *          type: integer
 *        Points:
 *          description: Points of the ChampionshipResultTableRaceResult
 *          type: integer
 *        Discarded:
 *          description: Discarded of the ChampionshipResultTableRaceResult
 *          type: boolean
 *        InputDate:
 *          description: Input Date of the ChampionshipResultTableRaceResult
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceResultCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableRaceResultParams'
 *        - type: object
 *          required:
 *            - ChampionshipResultTableRaceId
 *            - ChampionshipResultTableEntryId
 *            - Points
 *            - Discarded
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceResultQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the ChampionshipResultTableRaceResult
 *              type: string
 *        - $ref: '#/definitions/ChampionshipResultTableRaceResultParams'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceResultUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableRaceResultQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceResult:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableRaceResultCreateParams'
 *        - $ref: '#/definitions/ChampionshipResultTableRaceResultUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      ChampionshipResultTableRaceResult:
 *        allOf:
 *          - $ref: '#/definitions/ChampionshipResultTableRaceResult'
 *          - type: object
 *            properties:
 *              ChampionshipResultTableRace:
 *                description: ChampionshipResultTableRace of the ChampionshipResultTableRaceResult
 *                $ref: '#/components/schemas/ChampionshipResultTableRace'
 *              ChampionshipResultTableEntry:
 *                description: ChampionshipResultTableRace of the ChampionshipResultTableRaceResult
 *                $ref: '#/components/schemas/ChampionshipResultTableEntry'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ChampionshipResultTableEntryId: string;
  ChampionshipResultTableRaceId: string;
};

export type $Params = {
  Discarded: boolean;
  InputDate?: Date | null;
  Points: number;
  Value: null | number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  ChampionshipResultTableEntry?: $Model<'ChampionshipResultTableEntry'>;
  ChampionshipResultTableRace?: $Model<'ChampionshipResultTableRace'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Discarded: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  InputDate: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  Points: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      4,
      1,
    ),
    validate: {
      notEmpty: true,
    },
  },
  Value: {
    allowNull: true,
    type: Sequelize.DOUBLE(
      4,
      1,
    ),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'ChampionshipResultTableRaceResult'> => {
  const ChampionshipResultTableRaceResult = stack.define(
    'ChampionshipResultTableRaceResult',
    fields(Sequelize),
    {
      tableName: 'ChampionshipResultTableRaceResults',
    },
  );

  ChampionshipResultTableRaceResult.associate = (models: $ModelClasses) => {
    models.ChampionshipResultTableRaceResult.belongsTo(
      models.ChampionshipResultTableRace,
      {
        as: 'ChampionshipResultTableRace',
        foreignKey: 'ChampionshipResultTableRaceId',
      },
    );
    models.ChampionshipResultTableRaceResult.belongsTo(
      models.ChampionshipResultTableEntry,
      {
        as: 'ChampionshipResultTableEntry',
        foreignKey: 'ChampionshipResultTableEntryId',
      },
    );
  };

  ChampionshipResultTableRaceResult.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return ChampionshipResultTableRaceResult;
});
